import React, { useState } from 'react';
import Icon from '../Icon';
import './ui.css';

export interface MaskedValueProps {
  value: string;
  /** How many trailing characters stay visible while masked. Defaults to 4. */
  visibleChars?: number;
  className?: string;
  /** What the value is, for the toggle's label. Defaults to "reference". */
  label?: string;
}

/** Cap on the mask segment's dot-count so the masked string never mirrors
 *  the real value's exact length. */
const MAX_MASK_DOTS = 10;

/**
 * Displays a sensitive value (payment/settlement reference) masked by
 * default, with a click-to-reveal toggle — the same value must never be
 * shown in full without a deliberate click.
 */
export const MaskedValue: React.FC<MaskedValueProps> = ({ value, visibleChars = 4, className = '', label = 'reference' }) => {
  const [revealed, setRevealed] = useState(false);

  // slice(-0) would return the whole string, so a count of 0 must give an empty tail.
  const tail = visibleChars > 0 ? value.slice(-visibleChars) : '';
  const hiddenLength = Math.max(value.length - visibleChars, 0);
  // Always the same dot count when anything is hidden, so the mask never hints at the value's length.
  const dots = hiddenLength > 0 ? '•'.repeat(MAX_MASK_DOTS) : '';
  const masked = `${dots}${tail}`;

  return (
    <span className={`masked-value ${className}`.trim()}>
      <span className="masked-value-text">{revealed ? value : masked}</span>
      <button
        type="button"
        className="masked-value-toggle"
        title={revealed ? `Hide ${label}` : `Show ${label}`}
        aria-label={revealed ? `Hide ${label}` : `Show ${label}`}
        aria-pressed={revealed}
        onClick={() => setRevealed(r => !r)}
      >
        <Icon name={revealed ? 'eyeOff' : 'eye'} size={14} />
      </button>
    </span>
  );
};

export default MaskedValue;
